import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Modal,
  Tabs,
  Form,
  Input,
  Button,
  TimePicker,
  Select,
  Upload,
  Row,
  Col,
  Radio,
} from "antd";
import Notify from "../util/Notify";
import {
  ShopOutlined,
  EnvironmentOutlined,
  ContactsOutlined,
  UploadOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ShopServ from "../service/ShopServ";
import GlobalButton from "../util/GlobalButton";
import GlobalMap from "../util/GlobalMap";
import { ThemeContext } from "../util/ThemeContext";
import "../css/ChangeLocationContent.css";
import CategoryServ from "../service/CategoryServ";

import SafeImage, { getImageUrl } from './SafeImage';

dayjs.extend(customParseFormat);

const { TextArea } = Input;
const { Option } = Select;

const TABS = ['shopDetails', 'locations'];

// ─── Sequential Working Hours Picker ──────────────────────────────────────────
// Shows a styled "08:00 AM → 11:00 PM" display.
// Click → Opening Time picker opens.
// OK on Opening Time → Closing Time auto-fills & its picker opens immediately.
// OK on Closing Time → done.
const WorkingHoursPicker = ({ value, onChange }) => {
  const [openPicker, setOpenPicker] = useState(null); // null | 'opening' | 'closing'
  const [openingVal, setOpeningVal] = useState(null);
  const [closingVal, setClosingVal] = useState(null);
  const closingRef = useRef(null);

  // Sync incoming value (from form setFieldsValue)
  useEffect(() => {
    if (value && Array.isArray(value)) {
      setOpeningVal(value[0] || null);
      setClosingVal(value[1] || null);
    } else if (value && value.opening) {
      setOpeningVal(value.opening);
      setClosingVal(value.closing);
    } else if (!value) {
      setOpeningVal(null);
      setClosingVal(null);
    }
  }, [value]);

  const emit = (opening, closing) => {
    if (onChange) onChange([opening, closing]);
  };

  const handleOpeningOk = (val) => {
    setOpeningVal(val);
    // Auto-fill closing with same value if not set
    const newClosing = closingVal || val;
    setClosingVal(newClosing);
    emit(val, newClosing);
    // Close opening picker, open closing picker immediately
    setOpenPicker(null);
    setTimeout(() => setOpenPicker('closing'), 50);
  };

  const handleClosingOk = (val) => {
    setClosingVal(val);
    emit(openingVal, val);
    setOpenPicker(null);
  };

  const displayText = openingVal && closingVal ? {
    open: openingVal.format('hh:mm A'),
    close: closingVal.format('hh:mm A'),
  } : null;

  return (
    <div style={{ position: 'relative' }}>
      {/* ── Display / Trigger ── */}
      <div
        onClick={() => setOpenPicker('opening')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '7px 14px',
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          cursor: 'pointer',
          background: '#fff',
          minHeight: 42,
          transition: 'border-color 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#f97316'}
        onMouseLeave={e => e.currentTarget.style.borderColor = '#d9d9d9'}
      >
        <ClockCircleOutlined style={{ color: '#f97316', fontSize: 16 }} />
        {displayText ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>
              <span style={{ fontSize: 11, color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>Open</span>
              <span style={{ marginLeft: 5, fontWeight: 700, color: '#1a1a2e', fontSize: 14 }}>{displayText.open}</span>
            </span>
            <span style={{ color: '#f97316', fontWeight: 700, fontSize: 16, margin: '0 4px' }}>→</span>
            <span>
              <span style={{ fontSize: 11, color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>Close</span>
              <span style={{ marginLeft: 5, fontWeight: 700, color: '#1a1a2e', fontSize: 14 }}>{displayText.close}</span>
            </span>
          </span>
        ) : (
          <span style={{ color: '#bfbfbf' }}>Click to set opening &amp; closing time</span>
        )}
      </div>

      {/* ── Hidden Opening Time Picker ── */}
      <div style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', top: 0, left: 0 }}>
        <TimePicker
          open={openPicker === 'opening'}
          value={openingVal}
          format="hh:mm A"
          use12Hours
          onOk={handleOpeningOk}
          onOpenChange={(o) => { if (!o && openPicker === 'opening') setOpenPicker(null); }}
          getPopupContainer={() => document.body}
          popupStyle={{ zIndex: 1100 }}
        />
      </div>

      {/* ── Hidden Closing Time Picker ── */}
      <div ref={closingRef} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', top: 0, left: 0 }}>
        <TimePicker
          open={openPicker === 'closing'}
          value={closingVal}
          format="hh:mm A"
          use12Hours
          onOk={handleClosingOk}
          onOpenChange={(o) => { if (!o && openPicker === 'closing') setOpenPicker(null); }}
          getPopupContainer={() => document.body}
          popupStyle={{ zIndex: 1100 }}
        />
      </div>
    </div>
  );
};

// ─── Main AddShop Component ────────────────────────────────────────────────────
const AddShop = ({ open, refreshShops, shopDetails, isEditing }) => {
  const [form] = Form.useForm();
  const [imagePreview, setImagePreview] = useState("");
  const [imageMethod, setImageMethod] = useState("upload");
  const [activeTab, setActiveTab] = useState("shopDetails");
  const [submitting, setSubmitting] = useState(false);
  const [addresses, setAddresses] = useState([0]);
  const { darkMode } = useContext(ThemeContext);
  const [categories, setCategories] = useState([]);

  const defaultOpenTime = dayjs("08:00 AM", "hh:mm A");
  const defaultCloseTime = dayjs("08:00 PM", "hh:mm A");

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const formats = ["HH:mm:ss", "hh:mm A", "HH:mm", "h:mm A"];
    for (const fmt of formats) {
      const d = dayjs(timeStr, fmt, true);
      if (d.isValid()) return d;
    }
    const d = dayjs(timeStr);
    return d.isValid() ? d : null;
  };

  // Prefill or reset
  useEffect(() => {
    if (isEditing && shopDetails) {
      const location = shopDetails?.location || {};
      const coordinates = location.coordinates || [];
      const longitude = coordinates[0] || "";
      const latitude = coordinates[1] || "";

      const openTime = parseTime(shopDetails?.business?.openingTime) || defaultOpenTime;
      const closeTime = parseTime(shopDetails?.business?.closingTime) || defaultCloseTime;

      setTimeout(() => {
        form.setFieldsValue({
          shopId: shopDetails.shopId || "",
          name: shopDetails.name || "",
          description: shopDetails.description || "",
          imageUrl: shopDetails.imageUrl || "",
          email: shopDetails?.contact?.email || "",
          phone: shopDetails?.contact?.phone || "",
          workingHours: [openTime, closeTime],
          category: shopDetails?.business?.category || "",
          categoryId: (shopDetails?.business?.categoryId?._id || shopDetails?.business?.categoryId?.id || shopDetails?.business?.categoryId)?.toString() || undefined,
          subCategoryId: (shopDetails?.business?.subCategoryId?._id || shopDetails?.business?.subCategoryId?.id || shopDetails?.business?.subCategoryId)?.toString() || undefined,
          gstNumber: shopDetails?.business?.gstNumber || "",
          shopLicenseNumber: shopDetails?.business?.shopLicenseNumber || "",
          addresses: [{
            address: location.address || "",
            city: location.city || "",
            state: location.state || "",
            zipCode: location.zipCode || "",
            country: location.country || "",
            latitude,
            longitude,
          }],
        });
        setAddresses([0]);
      }, 200);

      const imgUrl = shopDetails.imageUrl || "";
      setImagePreview(imgUrl ? getImageUrl(imgUrl) : "");
      setImageMethod(imgUrl && (imgUrl.startsWith('http') || imgUrl.startsWith('https')) ? "url" : "upload");
    } else {
      form.resetFields();
      setImagePreview("");
      setImageMethod("upload");
      setAddresses([0]);
    }
  }, [isEditing, shopDetails, form, open]);

  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await ShopServ.upload(formData);
      const imageUrl = res.url || res.imageUrl || (res.data && (res.data.url || res.data.imageUrl));
      if (imageUrl) {
        setImagePreview(getImageUrl(imageUrl));
        form.setFieldsValue({ imageUrl });
        Notify.success("Image uploaded!");
      }
    } catch {
      Notify.error("Upload failed");
    }
    return false;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await CategoryServ.index({ limit: 'all' });
        let data = res?.data || res;
        if (!Array.isArray(data)) data = data.data || data.categories || data.items || [];
        setCategories(data.map(c => ({ ...c, _id: (c._id || c.id)?.toString(), id: (c.id || c._id)?.toString() })));
      } catch {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  const validateAllTabs = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      const errorFields = error.errorFields || [];
      if (errorFields.some(f => ['name', 'description', 'imageUrl', 'categoryId', 'subCategoryId'].includes(f.name[0]))) {
        setActiveTab('shopDetails'); Notify.error('Please complete Shop Details tab');
      } else if (errorFields.some(f => ['addresses', 'email', 'phone', 'workingHours'].includes(f.name[0]))) {
        setActiveTab('locations'); Notify.error('Please complete Locations & Contact info');
      }
      return false;
    }
  };

  const handleSubmit = async (values) => {
    try {
      const isValid = await validateAllTabs();
      if (!isValid) return;
      setSubmitting(true);

      const resolvedImageUrl = values.imageUrl || (imagePreview ? imagePreview.replace(/^https?:\/\/[^/]+/, '') : undefined);
      const [openingTime, closingTime] = values.workingHours || [];

      const payload = {
        name: values.name,
        description: values.description,
        imageUrl: resolvedImageUrl,
        contact: { phone: values.phone, email: values.email },
        business: {
          openingTime: openingTime?.format ? openingTime.format("HH:mm:ss") : undefined,
          closingTime: closingTime?.format ? closingTime.format("HH:mm:ss") : undefined,
          isOpen: true,
          category: categories.find(c => c._id?.toString() === values.categoryId?.toString())?.name || values.category,
          categoryId: values.categoryId,
          subCategoryId: values.subCategoryId,
          gstNumber: values.gstNumber,
          shopLicenseNumber: values.shopLicenseNumber,
        },
      };

      if (values.addresses?.[0]?.address) {
        const addr = values.addresses[0];
        payload.location = {
          address: addr.address, area: addr.area, city: addr.city,
          state: addr.state, zipCode: addr.zipCode, country: addr.country,
          coordinates: [parseFloat(addr.longitude) || 0, parseFloat(addr.latitude) || 0],
          latitude: parseFloat(addr.latitude) || 0,
          longitude: parseFloat(addr.longitude) || 0,
        };
      }

      const res = isEditing && shopDetails?._id
        ? await ShopServ.update(shopDetails._id, payload)
        : await ShopServ.create(payload);
      Notify.success(res.message || (isEditing ? "Shop updated!" : "Shop created!"));
      handleClose(true);
    } catch (err) {
      Notify.error(err, "Failed to save shop");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (isNew) => {
    refreshShops(isNew);
    form.resetFields();
    setActiveTab("shopDetails");
  };

  const handleManualAddressBlur = (index) => {
    const addrs = form.getFieldValue("addresses") || [];
    const addrData = addrs[index];
    if (!addrData) return;
    const fullAddr = [addrData.address, addrData.area, addrData.city, addrData.state, addrData.zipCode].filter(Boolean).join(", ");
    if (fullAddr && window.google) {
      new window.google.maps.Geocoder().geocode(
        { address: fullAddr, componentRestrictions: { country: "in" } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const loc = results[0].geometry.location;
            const updated = [...form.getFieldValue("addresses")];
            updated[index] = { ...updated[index], latitude: loc.lat(), longitude: loc.lng() };
            form.setFieldsValue({ addresses: updated });
          }
        }
      );
    }
  };

  const handleNext = async () => {
    try {
      if (activeTab === 'shopDetails') {
        await form.validateFields(['name', 'categoryId', 'gstNumber', 'shopLicenseNumber']);
      } else if (activeTab === 'locations') {
        await form.validateFields(['addresses', 'email', 'phone', 'workingHours']);
      }
      const i = TABS.indexOf(activeTab);
      if (i < TABS.length - 1) setActiveTab(TABS[i + 1]);
    } catch (err) {
      Notify.error("Please fill all required fields before proceeding");
    }
  };

  const handlePrevious = () => {
    const i = TABS.indexOf(activeTab);
    if (i > 0) setActiveTab(TABS[i - 1]);
  };

  const getFooterButtons = () => {
    const i = TABS.indexOf(activeTab);
    const isFirst = i === 0;
    const isLast = i === TABS.length - 1;
    return (
      <div key="footer" className="nz-form-footer">
        <div>
          {!isFirst && <GlobalButton action="edit" onClick={handlePrevious}>Previous</GlobalButton>}
        </div>
        <div className="pcu-flex pcu-gap-10">
          <GlobalButton action="cancel" onClick={() => handleClose(false)}>Cancel</GlobalButton>
          {!isLast
            ? <GlobalButton action="submit" onClick={handleNext}>Next</GlobalButton>
            : <GlobalButton action="submit" onClick={() => form.submit()} loading={submitting}>Submit</GlobalButton>
          }
        </div>
      </div>
    );
  };

  const addressesValues = Form.useWatch('addresses', form);

  // ✅ FIX: Detect if seller is resubmitting a rejected shop
  const isResubmitting = isEditing && shopDetails?.approvalStatus === 'rejected';

  const labelStyle = { fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 4 };

  return (
    <Modal
      open={open}
      onCancel={() => handleClose(false)}
      footer={null}
      width={720}
      centered
      closable={false}
      className={`ss-modal ${darkMode ? "dark" : "light"}`}
      bodyStyle={{ padding: 0 }}
      maskStyle={{ backdropFilter: 'blur(3px)' }}
      style={{ borderRadius: 12, overflow: 'hidden' }}
      styles={{ content: { padding: 0, borderRadius: 12, overflow: 'hidden' }, body: { padding: 0 } }}
    >
      <style>{`
        .ss-modal .ant-form-item-explain-error {
          font-size: 10px !important;
          font-weight: 700 !important;
          margin-top: 2px !important;
          color: #ef4444 !important;
          letter-spacing: 0.2px;
          text-transform: none;
        }
        .ss-modal .ant-form-item-has-error .ant-input,
        .ss-modal .ant-form-item-has-error .ant-select-selector {
          border-color: #fecaca !important;
          background: #fffafa !important;
        }
      `}</style>
      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
        {/* Header Section */}
        <div style={{ 
            padding: '12px 16px', 
            background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)', 
            borderBottom: '1px solid #e2e8f0', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
        }}>
            <div>
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isResubmitting ? "Edit & Resubmit Shop" : isEditing ? "Edit Shop" : "Create New Shop"}
                    {isResubmitting && (
                        <span style={{
                        fontSize: "10px", fontWeight: 800, background: "#fef2f2",
                        color: "#dc2626", border: "1px solid #fecaca", borderRadius: "4px",
                        padding: "2px 8px", textTransform: "uppercase"
                        }}>
                        Rejected — Resubmit Required
                        </span>
                    )}
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: 10, color: '#64748b', fontWeight: 600 }}>
                    {isEditing ? "Update shop information and configuration" : "Fill in details to register a new shop"}
                </p>
            </div>
            <button 
                onClick={() => handleClose(false)}
                style={{ background: '#fff', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 12, width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
            >
                <span style={{ fontSize: 12 }}>×</span>
            </button>
        </div>

        {/* Form Content */}
        <div style={{ padding: '16px 20px', maxHeight: '75vh', overflowY: 'auto' }}>
          <Form form={form} layout="vertical" size="small" requiredMark={false} onFinish={handleSubmit}>
            {/* ✅ FIX: Rejection context banner inside the form */}
            {isResubmitting && shopDetails?.rejectionReason && (
              <div style={{
                marginBottom: 16, padding: "12px 16px",
                background: "#fff1f2", border: "1px solid #fecdd3",
                borderRadius: "8px"
              }}>
                <div style={{ fontSize: "10px", fontWeight: 800, color: "#e11d48", textTransform: "uppercase", marginBottom: 4 }}>
                  ❌ Admin Rejection Reason
                </div>
                <div style={{ fontSize: "13px", color: "#9f1239" }}>
                  {shopDetails.rejectionReason}
                </div>
                <div style={{ fontSize: "11px", color: "#be123c", marginTop: 6 }}>
                  Please address the above issue and submit again for review.
                </div>
              </div>
            )}
            <Tabs activeKey={activeTab} onChange={(key) => {
              if (TABS.indexOf(key) > TABS.indexOf(activeTab)) {
                handleNext(); // handleNext already has the validation logic
              } else {
                setActiveTab(key);
              }
            }}>

              {/* ── Shop Details ── */}
              <Tabs.TabPane key="shopDetails" tab={<><ShopOutlined /> Shop Details</>}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="name" label={<span style={labelStyle}>Shop Name <span style={{color: '#ef4444'}}>*</span></span>} 
                      rules={[{ required: true, message: 'Please enter shop name' }]} style={{ marginBottom: 12 }}>
                      <Input placeholder="Shop Name" style={{ borderRadius: 6 }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="categoryId" label={<span style={labelStyle}>Category <span style={{color: '#ef4444'}}>*</span></span>} 
                      rules={[{ required: true, message: 'Please select a category' }]} style={{ marginBottom: 12 }}>
                      <Select placeholder="Select Category" showSearch style={{ borderRadius: 6 }}
                        filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}>
                        {categories.filter(c => !c.parentId).map(c => (
                          <Option key={c._id} value={c._id} label={c.name}>{c.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="gstNumber" label={<span style={labelStyle}>GST Number <span style={{color: '#ef4444'}}>*</span></span>} style={{ marginBottom: 12 }}
                      rules={[
                        { required: true, message: 'GST Number is required' },
                        { pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, message: "Please enter a valid GST number" }
                      ]}>
                      <Input placeholder="Enter GST Number" style={{ borderRadius: 6 }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="shopLicenseNumber" label={<span style={labelStyle}>Shop License Number <span style={{color: '#ef4444'}}>*</span></span>} 
                      rules={[{ required: true, message: 'License Number is required' }]} style={{ marginBottom: 12 }}>
                      <Input placeholder="Enter License Number" style={{ borderRadius: 6 }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="description" label={<span style={labelStyle}>Description</span>} style={{ marginBottom: 12 }}>
                      <TextArea rows={6} style={{ borderRadius: 6 }} placeholder="Enter shop description" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={<span style={labelStyle}>Shop Image Source</span>} style={{ marginBottom: 12 }}>
                      <Radio.Group value={imageMethod} onChange={e => setImageMethod(e.target.value)} style={{ marginBottom: 10 }}>
                        <Radio value="upload" style={{ fontSize: 12 }}>Upload Image</Radio>
                        <Radio value="url" style={{ fontSize: 12 }}>Use Image URL</Radio>
                      </Radio.Group>
                      <div style={{ marginTop: 4 }}>
                        {imageMethod === "upload" ? (
                          <div style={{ marginBottom: 12 }}>
                            <Upload beforeUpload={handleImageUpload} showUploadList={false} accept="image/*">
                              {imagePreview ? (
                                <div style={{ cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <SafeImage src={imagePreview} variant="thumbnail" style={{ borderRadius: 8, border: '1px solid #e2e8f0', maxWidth: '100%' }} />
                                  <div style={{ marginTop: 8, fontSize: 12, color: '#4776E6', fontWeight: 600 }}>Click to Change Logo</div>
                                </div>
                              ) : (
                                <div style={{
                                  width: '100%',
                                  padding: '16px',
                                  border: '1px dashed #cbd5e1',
                                  borderRadius: '8px',
                                  background: '#f8fafc',
                                  cursor: 'pointer',
                                  textAlign: 'center',
                                  transition: 'all 0.2s'
                                }} onMouseEnter={e => {
                                  e.currentTarget.style.borderColor = '#4776E6';
                                  e.currentTarget.style.background = '#f1f5f9';
                                }} onMouseLeave={e => {
                                  e.currentTarget.style.borderColor = '#cbd5e1';
                                  e.currentTarget.style.background = '#f8fafc';
                                }}>
                                  <UploadOutlined style={{ fontSize: 24, color: '#4776E6', marginBottom: 8 }} />
                                  <div style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Click to Upload Shop Logo</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8' }}>PNG, JPG up to 5MB</div>
                                </div>
                              )}
                            </Upload>
                          </div>
                        ) : (
                          <div style={{ marginBottom: 12 }}>
                            <Form.Item name="imageUrl" noStyle
                              rules={[{ validator: (_, v) => v && (v.startsWith('http') || v.startsWith('/uploads') || v.startsWith('uploads')) ? Promise.resolve() : Promise.reject('Please enter a valid image URL') }]}>
                              <Input placeholder="Enter URL" style={{ borderRadius: 6 }} onChange={e => { setImagePreview(e.target.value); form.setFieldsValue({ imageUrl: e.target.value }); }} />
                            </Form.Item>
                            {imagePreview && <div style={{ marginTop: 8 }}><SafeImage src={imagePreview} variant="thumbnail" /></div>}
                          </div>
                        )}
                        <Form.Item name="imageUrl" hidden><Input /></Form.Item>
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>

              {/* ── Locations ── */}
              <Tabs.TabPane key="locations" tab={<><EnvironmentOutlined /> Locations</>}>
                {addresses.map((_, index) => (
                  <div key={index} style={{ marginBottom: 16 }}>
                    <Row gutter={24}>
                      <Col span={12}>
                        <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                          <GlobalMap
                            height="280px"
                            onLocationSelect={data => {
                              const curr = form.getFieldValue(["addresses", index]) || {};
                              form.setFieldValue(["addresses", index], { ...curr, ...data });
                            }}
                            markerPosition={addressesValues?.[index]?.latitude
                              ? { lat: parseFloat(addressesValues[index].latitude), lng: parseFloat(addressesValues[index].longitude) }
                              : null}
                            address={addressesValues?.[index]?.address}
                          />
                        </div>
                      </Col>
                      <Col span={12}>
                        <Form.Item name={["addresses", index, "address"]} label={<span style={labelStyle}>Full Address <span style={{color: '#ef4444'}}>*</span></span>} 
                          rules={[{ required: true, message: 'Please enter shop address' }]} style={{ marginBottom: 12 }}>
                          <Input onBlur={() => handleManualAddressBlur(index)} style={{ borderRadius: 6 }} placeholder="e.g. 123 Main St" />
                        </Form.Item>
                        <Row gutter={16}>
                          <Col span={12}><Form.Item name={["addresses", index, "city"]} label={<span style={labelStyle}>City</span>} style={{ marginBottom: 12 }}><Input style={{ borderRadius: 6 }} /></Form.Item></Col>
                          <Col span={12}><Form.Item name={["addresses", index, "zipCode"]} label={<span style={labelStyle}>Zip Code</span>} style={{ marginBottom: 12 }}><Input style={{ borderRadius: 6 }} /></Form.Item></Col>
                        </Row>
                        <Row gutter={16}>
                          <Col span={12}><Form.Item name={["addresses", index, "latitude"]} label={<span style={labelStyle}>Latitude</span>} style={{ marginBottom: 12 }}><Input readOnly style={{ borderRadius: 6, background: '#f8fafc' }} /></Form.Item></Col>
                          <Col span={12}><Form.Item name={["addresses", index, "longitude"]} label={<span style={labelStyle}>Longitude</span>} style={{ marginBottom: 12 }}><Input readOnly style={{ borderRadius: 6, background: '#f8fafc' }} /></Form.Item></Col>
                        </Row>
                        <Form.Item name={["addresses", index, "state"]} hidden><Input /></Form.Item>
                        <Form.Item name={["addresses", index, "country"]} hidden><Input /></Form.Item>
                        <Form.Item name={["addresses", index, "area"]} hidden><Input /></Form.Item>
                        
                        {/* Merged Contact Info */}
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item name="email" label={<span style={labelStyle}>Email <span style={{color: '#ef4444'}}>*</span></span>} 
                                rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Enter a valid email' }]} style={{ marginBottom: 12 }}>
                                <Input style={{ borderRadius: 6 }} placeholder="shop@example.com" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item name="phone" label={<span style={labelStyle}>Phone <span style={{color: '#ef4444'}}>*</span></span>} 
                                rules={[{ required: true, message: 'Phone is required' }]} style={{ marginBottom: 12 }}>
                                <Input style={{ borderRadius: 6 }} placeholder="Phone number" />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Form.Item
                            name="workingHours"
                            label={<span style={labelStyle}>Working Hours <span style={{color: '#ef4444'}}>*</span></span>}
                            rules={[{
                              validator: (_, v) =>
                                v && Array.isArray(v) && v[0] && v[1]
                                  ? Promise.resolve()
                                  : Promise.reject('Select working hours')
                            }]}
                            style={{ marginBottom: 0 }}
                          >
                            <WorkingHoursPicker />
                          </Form.Item>
                        </div>
                      </Col>
                    </Row>
                  </div>
                ))}
              </Tabs.TabPane>

            </Tabs>
          </Form>
        </div>

        {/* Footer Actions */}
        <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {TABS.indexOf(activeTab) > 0 && (
                <button onClick={handlePrevious} type="button" style={{
                  padding: '6px 16px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#475569', cursor: 'pointer'
                }}>
                  Previous
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => handleClose(false)} type="button" style={{
                    padding: '6px 16px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#475569', cursor: 'pointer'
                }}>
                    Cancel
                </button>
                {TABS.indexOf(activeTab) < TABS.length - 1 ? (
                  <button onClick={handleNext} type="button" style={{
                      padding: '6px 20px', background: 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer', boxShadow: '0 2px 4px rgba(71,118,230,0.2)'
                  }}>
                      Next
                  </button>
                ) : (
                  <button onClick={() => form.submit()} type="submit" disabled={submitting} style={{
                      padding: '6px 20px', background: 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer', boxShadow: '0 2px 4px rgba(71,118,230,0.2)', opacity: submitting ? 0.7 : 1
                  }}>
                      {submitting ? 'Submitting...' : isResubmitting ? 'Resubmit Shop' : isEditing ? 'Update Shop' : 'Create Shop'}
                  </button>
                )}
            </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddShop;
